import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, AppThunk } from "../../app/store";
import { fetchCount } from "./counterAPI";

export interface CounterState {
	value: number;
	status: "idle" | "loading" | "failed";
}

const initialState: CounterState = {
	value: 0,
	status: "idle"
};

/**
 * 下面的函數稱為 thunk，它允許我們執行異步邏輯。
 * 它可以像常規操作一樣分派：`dispatch(incrementAsync(10))`。
 *  這將使用 `dispatch` 函數作為第一個參數調用 thunk。 異步
 * 然後可以執行代碼並分派其他操作。 重點是通常用於發出異步請求。
 */

export const incrementAsync = createAsyncThunk(
	"counter/fetchCount",
	async (amount: number) => {
		const response = await fetchCount(amount);

		// The value we return becomes the `fulfilled` action payload
		return response.data;
	}
);

export const counterSlice = createSlice({
	name: "counter",
	initialState,
	// `reducers` 字段讓我們定義 reducers 並生成關聯的操作
	reducers: {
		increment: state => {
			/**
       * Redux Toolkit 允許我們在 reducer 中編寫 `mutating state` 邏輯。
       * 它實際上沒有改變狀態，因為它使用了 `Immer` 函式庫，
       * 它檢測到 `draft state` 的變化並基於這些改變產生一個全新的 `immutable state`
       * 273 / 5,000

       */
			state.value += 1;
		},
		decrement: state => {
			state.value -= 1;
		},
		// Use the PayloadAction type to declare the contents of `action.payload`
		incrementByAmount: (state, action: PayloadAction<number>) => {
			state.value += action.payload;
		},
		resetCounter: state => {
			state.value = 0;
		}
	},
	// The `extraReducers` field lets the slice handle actions defined elsewhere,
	// including actions generated by createAsyncThunk or in other slices.
	extraReducers: builder => {
		builder
			.addCase(incrementAsync.pending, state => {
				state.status = "loading";
			})
			.addCase(incrementAsync.fulfilled, (state, action) => {
				state.status = "idle";
				state.value += action.payload;
			})
			.addCase(incrementAsync.rejected, (state, action) => {
				state.status = "failed";
				state.value = 0;
				console.log(action.error.message);
			});
	}
});

export const { increment, decrement, incrementByAmount, resetCounter } =
	counterSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const incrementIfOdd =
	(amount: number): AppThunk =>
	(dispatch, getState) => {
		const currentValue = selectCount(getState());
		if (currentValue % 2 === 1) {
			dispatch(incrementByAmount(amount));
		}
	};

export default counterSlice.reducer;
